import React, { useState } from "react";
import axios from "axios";
import { Mail, Truck, Headphones, Send, Check } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in name, email, and message");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            setDone(true);
            setForm({ name: "", email: "", subject: "", message: "" });
            toast.success("Message sent! We'll get back to you within 24 hours.");
        } catch (err) {
            toast.error("Something went wrong. Please try again or email us directly.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="pt-32 pb-24 min-h-screen" data-testid="contact-page">
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-16">
                <div>
                    <p className="text-pep-primary text-sm uppercase tracking-widest font-medium mb-3">Get in touch</p>
                    <h1 className="font-heading text-5xl sm:text-6xl tracking-tighter text-pep-navy font-medium leading-[0.95]">
                        Let's talk research.
                    </h1>
                    <p className="text-pep-muted mt-6 text-lg leading-relaxed">
                        Questions about our peptides, bulk orders, or technical support?
                        Our team typically responds within a few hours during business days.
                    </p>

                    <div className="mt-12 space-y-6">
                        <ContactItem
                            icon={Mail}
                            title="Email"
                            content="orders@pep-step.com"
                            href="mailto:orders@pep-step.com"
                            testid="contact-email"
                        />
                        <ContactItem
                            icon={Truck}
                            title="Shipping"
                            content="48-hour dispatch · Tracked international shipping · Cold-chain when required"
                            testid="contact-shipping"
                        />
                        <ContactItem
                            icon={Headphones}
                            title="Support hours"
                            content="Monday – Friday · 9am – 6pm GMT"
                            testid="contact-hours"
                        />
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    className="bg-pep-surface rounded-3xl p-8 md:p-10 border border-pep-border/50 h-fit"
                    data-testid="contact-form"
                >
                    <h2 className="font-heading text-2xl font-medium text-pep-navy mb-6">Send us a message</h2>
                    {done ? (
                        <div className="text-center py-12" data-testid="contact-success">
                            <div className="w-16 h-16 rounded-full bg-pep-primary/10 text-pep-primary flex items-center justify-center mx-auto mb-4">
                                <Check className="w-6 h-6" />
                            </div>
                            <p className="font-heading text-xl text-pep-navy">Message sent!</p>
                            <p className="text-pep-muted mt-2">We'll get back to you within 24 hours.</p>
                            <button
                                type="button"
                                onClick={() => setDone(false)}
                                className="mt-6 text-pep-primary font-medium"
                            >
                                Send another message
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm text-pep-navy font-medium mb-1.5 block">Name</label>
                                <Input
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder="Your full name"
                                    className="rounded-xl bg-white border-pep-border focus-visible:ring-pep-primary"
                                    data-testid="contact-name"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-pep-navy font-medium mb-1.5 block">Email</label>
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    placeholder="you@lab.com"
                                    className="rounded-xl bg-white border-pep-border focus-visible:ring-pep-primary"
                                    data-testid="contact-email-input"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-pep-navy font-medium mb-1.5 block">Subject</label>
                                <Input
                                    value={form.subject}
                                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                    placeholder="What can we help with?"
                                    className="rounded-xl bg-white border-pep-border focus-visible:ring-pep-primary"
                                    data-testid="contact-subject"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-pep-navy font-medium mb-1.5 block">Message</label>
                                <Textarea
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    rows={5}
                                    placeholder="Tell us about your research..."
                                    className="rounded-xl bg-white border-pep-border focus-visible:ring-pep-primary"
                                    data-testid="contact-message"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-pep-primary text-white rounded-full py-4 font-medium hover:bg-pep-navy transition disabled:opacity-50 flex items-center justify-center gap-2"
                                data-testid="contact-submit"
                            >
                                {submitting ? "Sending..." : <>Send Message <Send className="w-4 h-4" /></>}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}

function ContactItem({ icon: Icon, title, content, href, testid }) {
    const Wrapper = href ? "a" : "div";
    const props = href ? { href } : {};
    return (
        <Wrapper {...props} className="flex gap-4 group" data-testid={testid}>
            <div className="w-12 h-12 rounded-2xl bg-white border border-pep-border/60 text-pep-primary flex items-center justify-center group-hover:bg-pep-primary group-hover:text-white transition flex-shrink-0">
                <Icon className="w-5 h-5" />
            </div>
            <div>
                <p className="text-xs uppercase tracking-widest text-pep-muted mb-1">{title}</p>
                <p className="text-pep-navy font-medium">{content}</p>
            </div>
        </Wrapper>
    );
}
